<template>
  <div class="tracking-page" :class="{ 'trk-page-busy': loading }">
    <tracking-page-header
      title="All booking journeys"
      subtitle="Filter by date, source, step, or status. Click a row to open the full timeline."
      :from-date="fromDate"
      :to-date="toDate"
      :loading="loading"
      :chat-filters="chatFilters"
      :page-summary="pageSummary"
      @apply="fromDate = $event.fromDate; toDate = $event.toDate; page = 1; syncAndLoad()"
      @refresh="refresh"
    />

    <conversion-insight-card :from="fromDate" :to="toDate" />

    <div class="trk-panel mb-3">
      <div class="trk-panel-head trk-panel-head-split">
        <div>
          <h2>Booked vs consult completed, per day</h2>
          <p>
            <strong class="trk-booked-ink">Booked</strong> = order placed that day.
            <strong class="trk-completed-ink">Completed</strong> = the doctor finished the consultation that day (by appointment time).
            Click a number to list those journeys.
          </p>
        </div>
      </div>
      <div class="trk-panel-body" :class="{ 'trk-panel-body--loading': dailyLoading }">
        <div class="trk-outcome-tiles">
          <button
            type="button"
            class="trk-outcome-tile trk-outcome-booked"
            :class="{ active: filters.dateBasis === 'booked' }"
            title="List journeys booked in this date range"
            @click="toggleDateBasis('booked')"
          >
            <span class="trk-outcome-label"><i class="fa fa-calendar-check-o"></i> Booked</span>
            <span class="trk-outcome-value">{{ formatNumber(daily.totalBooked) }}</span>
            <span class="trk-outcome-help">orders placed {{ rangeLabel }}</span>
          </button>
          <button
            type="button"
            class="trk-outcome-tile trk-outcome-completed"
            :class="{ active: filters.dateBasis === 'completed' }"
            title="List journeys whose consultation was completed in this date range"
            @click="toggleDateBasis('completed')"
          >
            <span class="trk-outcome-label"><i class="fa fa-check-circle"></i> Consult completed</span>
            <span class="trk-outcome-value">{{ formatNumber(daily.totalCompleted) }}</span>
            <span class="trk-outcome-help">consultations done {{ rangeLabel }}</span>
          </button>
        </div>
        <p v-if="daily.truncated" class="text-muted small mb-2">Over 5,000 journeys in one of these counts — totals are a minimum. Narrow the date range for exact numbers.</p>
        <div class="trk-daily-scroll" v-if="daily.days.length > 1">
          <table class="table trk-table trk-daily-table mb-0">
            <thead>
              <tr>
                <th>Day (UK)</th>
                <th class="trk-booked-ink">Booked</th>
                <th class="trk-completed-ink">Consult completed</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="day in daily.days" :key="day.date" :class="{ 'trk-daily-selected': isSelectedDay(day.date) }">
                <td>{{ formatDay(day.date) }}</td>
                <td>
                  <button type="button" class="trk-daily-count" :disabled="!day.booked" @click="showDay(day.date, 'booked')">
                    <span class="trk-daily-bar trk-daily-bar-booked" :style="{ width: barWidth(day.booked) }"></span>
                    <span class="trk-daily-num">{{ formatNumber(day.booked) }}</span>
                  </button>
                </td>
                <td>
                  <button type="button" class="trk-daily-count" :disabled="!day.completed" @click="showDay(day.date, 'completed')">
                    <span class="trk-daily-bar trk-daily-bar-completed" :style="{ width: barWidth(day.completed) }"></span>
                    <span class="trk-daily-num">{{ formatNumber(day.completed) }}</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="trk-panel mb-3">
      <div class="trk-panel-head trk-panel-head-split">
        <div>
          <h2>Why journeys didn't book</h2>
          <p>
            {{ formatNumber(reasons.totalNotConverted) }} journeys started {{ rangeLabel }} and left without booking<span v-if="reasons.stillOnSite"> · {{ formatNumber(reasons.stillOnSite) }} still on the site</span>.
            Worked out from the last error each person hit. Click a reason to list those journeys.
          </p>
        </div>
      </div>
      <div class="trk-panel-body" :class="{ 'trk-panel-body--loading': reasonsLoading }">
        <p v-if="reasons.truncated" class="text-muted small">Over 5,000 unbooked journeys — only the most recent 5,000 are included. Narrow the date range for exact numbers.</p>
        <div v-if="!reasons.reasons.length && !reasonsLoading" class="trk-empty"><strong>Nothing to explain</strong>No unbooked journeys in this range.</div>
        <div class="trk-reason-list">
          <button
            v-for="r in reasons.reasons"
            :key="r.code"
            type="button"
            class="trk-reason-row"
            :class="{ active: filters.stopReason === r.code }"
            @click="toggleStopReason(r.code)"
          >
            <span class="trk-reason-main">
              <span class="trk-reason-label">{{ r.label }}</span>
              <span class="trk-pill ml-2" :class="stopReasonClass(r.side)" v-if="stopSideLabel(r.side)">{{ stopSideLabel(r.side) }}</span>
              <span class="trk-reason-steps" v-if="r.topSteps && r.topSteps.length">at {{ r.topSteps.map(stepName).join(', ') }}</span>
            </span>
            <span class="trk-reason-bar"><span :class="'trk-reason-fill-' + stopReasonClass(r.side)" :style="{ width: Math.max(2, r.percent) + '%' }"></span></span>
            <span class="trk-reason-count">{{ formatNumber(r.count) }} <small>{{ r.percent }}%</small></span>
            <span class="trk-reason-details" v-if="r.topDetails && r.topDetails.length">
              <span v-for="d in r.topDetails" :key="d.detail" :title="d.detail">
                “{{ d.detail }}” <em>×{{ formatNumber(d.count) }}</em>
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>

    <div class="trk-panel mb-3">
      <div class="trk-panel-head trk-panel-head-split">
        <div>
          <h2>Find journeys</h2>
          <p v-if="appliedChips.length">Active filters: {{ appliedChips.join(' · ') }}</p>
          <p v-else>Showing all journeys in the selected date range</p>
        </div>
        <div class="trk-panel-actions">
          <button
            class="btn trk-btn-ghost btn-sm"
            type="button"
            :disabled="loading || exporting"
            title="This table only, as CSV"
            @click="downloadJourneys"
          >
            <i class="fa fa-download"></i> CSV
          </button>
          <button
            class="btn trk-btn-primary btn-sm"
            type="button"
            :disabled="loading || exporting"
            title="ZIP with journeys, steps, funnel, problems, APIs, slots and fixes"
            @click="downloadEverything"
          >
            <i class="fa" :class="exporting ? 'fa-spinner fa-spin' : 'fa-file-archive-o'"></i>
            {{ exporting ? 'Preparing ZIP…' : 'Download ZIP' }}
          </button>
          <button
            v-if="hasActiveFilters"
            class="btn trk-btn-ghost btn-sm"
            type="button"
            @click="clearFilters"
          >
            Clear filters
          </button>
        </div>
      </div>
      <div class="trk-panel-body">
        <div class="trk-filter-grid">
          <label class="trk-filter-field trk-filter-field-wide">
            <span>Search</span>
            <div class="trk-toolbar-search">
              <i class="fa fa-search trk-toolbar-search-icon"></i>
              <input
                class="form-control"
                v-model="filters.q"
                placeholder="Name, email, phone or session ID"
                @keyup.enter="page = 1; syncAndLoad()"
              />
            </div>
          </label>
          <label class="trk-filter-field">
            <span>Source</span>
            <select class="form-control" v-model="filters.source" @change="page = 1; syncAndLoad()">
              <option value="">All sources</option>
              <option v-for="opt in sourceOptions" :key="opt.value" :value="opt.value" :title="opt.hint">{{ opt.label }}</option>
            </select>
          </label>
          <label class="trk-filter-field">
            <span>Show</span>
            <select
              class="form-control"
              v-model="filters.booked"
              title="Booked = order placed in the date range. Consult completed = doctor finished the consultation in the date range."
              @change="onConsultOutcomeChange"
            >
              <option value="">All journeys</option>
              <option v-for="opt in bookingOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </label>
          <label class="trk-filter-field">
            <span>Why not booked</span>
            <select
              class="form-control"
              v-model="filters.stopReason"
              title="The last error they hit before leaving. Picking a reason shows unbooked journeys only."
              @change="onStopReasonChange"
            >
              <option value="">Any reason</option>
              <option v-for="opt in stopReasonOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </label>
          <label class="trk-filter-field">
            <span>Phone / call</span>
            <select
              class="form-control"
              v-model="filters.callStatus"
              title="People who left a phone number, and whether someone has called them"
              @change="page = 1; syncAndLoad()"
            >
              <option value="">Anyone</option>
              <option v-for="opt in callStatusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </label>
          <label class="trk-filter-field">
            <span>Device</span>
            <select class="form-control" v-model="filters.deviceType" @change="page = 1; syncAndLoad()">
              <option value="">Any</option>
              <option value="mobile">Mobile</option>
              <option value="desktop">Desktop</option>
              <option value="tablet">Tablet</option>
            </select>
          </label>
          <label class="trk-filter-field">
            <span>Entry step</span>
            <select
              class="form-control"
              v-model="filters.entrySurface"
              title="Where the journey started"
              @change="page = 1; syncAndLoad()"
            >
              <option value="">Any entry</option>
              <option v-for="opt in entryOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </label>
          <label class="trk-filter-field">
            <span>Current step</span>
            <select class="form-control" v-model="filters.step" @change="page = 1; syncAndLoad()">
              <option value="">Any step</option>
              <option v-for="opt in stepOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </label>
          <label class="trk-filter-field">
            <span>Status</span>
            <select class="form-control" v-model="filters.status" @change="page = 1; syncAndLoad()">
              <option value="">Any status</option>
              <option value="ACTIVE">Browsing now</option>
              <option value="IDLE">Paused</option>
              <option value="ABANDONED">Left without finishing</option>
              <option value="COMPLETED">Journey finished (checkout done)</option>
            </select>
          </label>
        </div>
        <details class="trk-export-help mt-2">
          <summary>What is in the ZIP download?</summary>
          <p>
            Filtered journeys with every tracked step inside them, plus dashboard data for the date range:
            funnel, problems, API performance, doctor slots, support calls and fixes — each as its own sheet.
          </p>
        </details>
      </div>
    </div>

    <div class="trk-panel" ref="journeysPanel">
      <div class="trk-panel-head trk-panel-head-split">
        <div>
          <h2>Journeys</h2>
          <p>{{ formatNumber(totalElements) }} match your filters</p>
        </div>
      </div>
      <div class="trk-panel-body p-0">
        <div class="trk-table-scroll">
          <table class="table trk-table trk-table-clickable trk-table-wide mb-0" v-trk-resizable="'journeys'">
            <thead>
              <tr>
                <th :class="colClass('status')" :title="colTitle('status')">Status</th>
                <th :class="colClass('firstName')" :title="colTitle('firstName')">First name</th>
                <th :class="colClass('lastName')" :title="colTitle('lastName')">Last name</th>
                <th :class="colClass('email')" :title="colTitle('email')">Email</th>
                <th :class="colClass('phone')" :title="colTitle('phone')">Phone</th>
                <th :class="colClass('source')" :title="colTitle('source')">Source</th>
                <th>Type</th>
                <th :class="colClass('booked')" :title="colTitle('booked') || 'Booked = order placed. Consult completed = doctor finished the consultation. Times are UK.'">Booking</th>
                <th :class="colClass('entry')" :title="colTitle('entry')">Entry</th>
                <th :class="colClass('step')" :title="colTitle('step')">Current step</th>
                <th :class="colClass('deviceType')" :title="colTitle('deviceType')">Device</th>
                <th title="UK time (Europe/London)">Started (UK)</th>
                <th title="UK time (Europe/London)">Last activity (UK)</th>
                <th :class="colClass('session')" :title="colTitle('session')">Session</th>
                <th :class="colClass('call')" :title="colTitle('call') || 'Tick once the person has been called, and add why / the outcome'">Call</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in sessions"
                :key="row.sessionId"
                @click="openJourney(row.sessionId)"
              >
                <td :class="colClass('status')">
                  <span class="trk-pill" :class="statusClass(row.status)" :title="journeyStatusTitle(row)">{{ prettyJourneyStatus(row) }}</span>
                  <span class="trk-pill info ml-1" v-if="row.previousSessionId" title="Return visit">Return</span>
                </td>
                <td :class="colClass('firstName')">{{ row.leadFirstName || '-' }}</td>
                <td :class="colClass('lastName')">{{ row.leadLastName || '-' }}</td>
                <td :class="colClass('email')">{{ row.leadEmail || '-' }}</td>
                <td :class="colClass('phone')">{{ row.leadPhone || '-' }}</td>
                <td :class="colClass('source')" :title="sourceHint(row.sourceKey, row)">{{ formatSource(row) }}</td>
                <td>
                  <span class="trk-pill info" v-if="row.userType">{{ prettyUserType(row.userType) }}</span>
                  <span v-else>-</span>
                </td>
                <td :class="colClass('booked')">
                  <span class="trk-pill" :class="bookingOutcomeClass(row)">
                    <i class="fa" :class="bookingIcon(row)"></i> {{ prettyBookingOutcome(row) }}
                  </span>
                  <div class="trk-booking-when" v-if="row.bookedAt">
                    <span class="trk-booked-ink">Booked</span> {{ formatShort(row.bookedAt) }}
                  </div>
                  <div class="trk-booking-when" v-if="row.consultCompletedAt">
                    <span class="trk-completed-ink">Completed</span> {{ formatShort(row.consultCompletedAt) }}
                  </div>
                  <div class="trk-booking-when" v-else-if="row.appointmentAt">
                    Appt {{ formatShort(row.appointmentAt) }}
                  </div>
                  <div class="trk-stop-reason" v-if="row.stopReason" :title="row.stopReasonDetail || ''">
                    <span class="trk-pill" :class="stopReasonClass(row.stopReasonSide)">{{ row.stopReasonLabel }}</span>
                    <span class="trk-stop-detail" v-if="row.stopReasonDetail">{{ row.stopReasonDetail }}</span>
                    <span class="trk-stop-detail" v-else-if="row.stopReasonStep">on {{ stepName(row.stopReasonStep) }}</span>
                  </div>
                </td>
                <td :class="colClass('entry')">{{ prettyEntrySurface(row.entrySurface) }}</td>
                <td :class="colClass('step')">{{ prettyStep(row.currentStep) }}</td>
                <td class="text-capitalize" :class="colClass('deviceType')">{{ row.deviceType || '-' }}</td>
                <td>{{ formatWhen(row.startedAt) }}</td>
                <td>{{ formatWhen(row.lastActivityAt) }}</td>
                <td :class="colClass('session')"><code class="trk-session-id" :title="row.sessionId">{{ shortId(row.sessionId) }}</code></td>
                <td class="trk-call-cell" :class="[colClass('call'), { 'trk-call-done': row.called }]" @click.stop>
                  <label class="trk-call-check">
                    <input
                      type="checkbox"
                      :checked="row.called"
                      :disabled="isCallSaving(row)"
                      @change="saveCall(row, $event.target.checked)"
                    />
                    {{ row.called ? 'Called' : 'Mark as called' }}
                  </label>
                  <textarea
                    class="form-control form-control-sm trk-call-note"
                    rows="2"
                    maxlength="1000"
                    placeholder="Reason / outcome of the call"
                    :value="callNote(row)"
                    :disabled="isCallSaving(row)"
                    @input="setCallNote(row, $event.target.value)"
                  ></textarea>
                  <div class="trk-call-actions" v-if="isCallNoteDirty(row) || callErrors[row.sessionId]">
                    <button
                      type="button"
                      class="btn trk-btn-primary btn-sm"
                      :disabled="isCallSaving(row) || !isCallNoteDirty(row)"
                      @click="saveCall(row, row.called)"
                    >
                      <i class="fa" :class="isCallSaving(row) ? 'fa-spinner fa-spin' : 'fa-check'"></i> Save note
                    </button>
                    <span class="trk-call-error" v-if="callErrors[row.sessionId]">{{ callErrors[row.sessionId] }}</span>
                  </div>
                  <div class="trk-call-meta" v-if="row.callUpdatedAt">
                    {{ row.callUpdatedBy || 'Admin' }} · {{ formatShort(row.callUpdatedAt) }}
                  </div>
                </td>
                <td class="text-right" @click.stop>
                  <router-link class="btn trk-btn-ghost btn-sm" :to="journeyLink(row.sessionId)">Open</router-link>
                </td>
              </tr>
              <tr v-if="!loading && !sessions.length">
                <td colspan="16"><div class="trk-empty"><strong>No journeys found</strong>Try a wider date range, clear filters, or search by name / email / phone.</div></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="trk-panel-body d-flex justify-content-between align-items-center flex-wrap" v-if="totalElements > 0">
        <span class="text-muted small">{{ paginationLabel }}</span>
        <b-pagination
          v-if="totalElements > size"
          v-model="page"
          :total-rows="totalElements"
          :per-page="size"
          @change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TrackingPageHeader from './TrackingPageHeader.vue';
import ConversionInsightCard from './ConversionInsightCard.vue';
import {
  prettyStatus,
  prettyJourneyStatus,
  journeyStatusTitle,
  isBooked,
  prettyBookingOutcome,
  bookingOutcomeClass,
  bookingOutcome,
  BOOKING_FILTER_OPTIONS,
  STOP_REASON_OPTIONS,
  stopReasonClass,
  stopSideLabel,
  prettyStep,
  prettyUserType,
  prettyEntrySurface,
  defaultDateRange,
  rangeParams,
  formatUkDateTime,
  formatNumber,
  formatReferrer,
  sourceHint,
  SOURCE_FILTER_OPTIONS,
  PATIENT_STEP_FILTER_OPTIONS,
  ENTRY_SURFACE_FILTER_OPTIONS,
  CALL_STATUS_FILTER_OPTIONS,
  applyJourneyQuery,
  journeyQuery,
  describeAppliedFilters
} from './trackingShared';

function emptyFilters() {
  return {
    status: '',
    q: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    step: '',
    deviceType: '',
    source: '',
    booked: '',
    dateBasis: '',
    stopReason: '',
    entrySurface: '',
    callStatus: ''
  };
}

function emptyReasons() {
  return { totalNotConverted: 0, stillOnSite: 0, reasons: [], truncated: false };
}

function emptyDaily() {
  return { days: [], totalBooked: 0, totalCompleted: 0, truncated: false };
}

export default {
  name: 'Sessions',
  components: { TrackingPageHeader, ConversionInsightCard },
  data() {
    return {
      ...defaultDateRange(7),
      sessions: [],
      totalElements: 0,
      page: 1,
      size: 20,
      loading: false,
      exporting: false,
      sourceOptions: SOURCE_FILTER_OPTIONS,
      stepOptions: PATIENT_STEP_FILTER_OPTIONS,
      entryOptions: ENTRY_SURFACE_FILTER_OPTIONS,
      bookingOptions: BOOKING_FILTER_OPTIONS,
      stopReasonOptions: STOP_REASON_OPTIONS,
      callStatusOptions: CALL_STATUS_FILTER_OPTIONS,
      /** Unsaved call notes and in-flight saves, by session id. */
      callNotes: {},
      callSaving: {},
      callErrors: {},
      reasons: emptyReasons(),
      reasonsLoading: false,
      reasonsRequestId: 0,
      daily: emptyDaily(),
      dailyLoading: false,
      dailyKey: null,
      dailyRequestId: 0,
      filters: emptyFilters()
    };
  },
  created() {
    applyJourneyQuery((this.$route && this.$route.query) || {}, this);
    if (this.filters.booked === 'yes') {
      this.filters.dateBasis = 'booked';
    } else if (this.filters.booked === 'completed') {
      this.filters.dateBasis = 'completed';
    }
    this.loadSessions();
  },
  computed: {
    appliedChips() {
      return describeAppliedFilters(this.fromDate, this.toDate, this.filters);
    },
    hasActiveFilters() {
      const f = this.filters;
      return !!(f.status || f.q || f.firstName || f.lastName || f.email || f.phone
        || f.step || f.deviceType || f.source || f.booked || f.dateBasis || f.stopReason || f.entrySurface || f.callStatus);
    },
    filteredColumns() {
      const f = this.filters;
      const columns = {};
      const add = (key, reason) => {
        columns[key] = columns[key] ? columns[key] + '. ' + reason : reason;
      };

      if (f.status) add('status', 'Filtered by status');
      if (f.source) add('source', 'Filtered by source');
      if (f.booked) add('booked', 'Filtered by booking');
      if (f.stopReason) add('booked', 'Filtered by why not booked');
      if (f.dateBasis === 'booked') add('booked', 'Date range applies to the day it was booked');
      if (f.dateBasis === 'completed') add('booked', 'Date range applies to the day the consult was completed');
      if (f.deviceType) add('deviceType', 'Filtered by device');
      if (f.entrySurface) add('entry', 'Filtered by entry step');
      if (f.step) add('step', 'Filtered by current step');
      if (f.firstName) add('firstName', 'Filtered by first name');
      if (f.lastName) add('lastName', 'Filtered by last name');
      if (f.email) add('email', 'Filtered by email');
      if (f.phone) add('phone', 'Filtered by phone');
      if (f.callStatus) add(f.callStatus === 'has_phone' ? 'phone' : 'call', 'Filtered by phone / call');
      if (f.callStatus === 'to_call') add('phone', 'Filtered by phone / call');
      if (f.q) {
        ['firstName', 'lastName', 'email', 'phone', 'session'].forEach((key) => {
          add(key, 'Searched for "' + f.q + '"');
        });
      }
      return columns;
    },
    filteredColumnCount() {
      return Object.keys(this.filteredColumns).length;
    },
    chatFilters() {
      return {
        ...rangeParams(this.fromDate, this.toDate),
        status: this.filters.status || undefined,
        step: this.filters.step || undefined,
        q: this.filters.q || undefined,
        firstName: this.filters.firstName || undefined,
        lastName: this.filters.lastName || undefined,
        email: this.filters.email || undefined,
        phone: this.filters.phone || undefined,
        deviceType: this.filters.deviceType || undefined,
        source: this.filters.source || undefined,
        booked: this.filters.booked || undefined,
        dateBasis: this.filters.dateBasis || undefined,
        stopReason: this.filters.stopReason || undefined,
        entrySurface: this.filters.entrySurface || undefined,
        callStatus: this.filters.callStatus || undefined
      };
    },
    dailyParams() {
      // Booking filters are what the daily panel measures, so they are left out here.
      const params = Object.assign({}, this.chatFilters);
      delete params.booked;
      delete params.dateBasis;
      delete params.stopReason;
      delete params.callStatus;
      return params;
    },
    dailyMax() {
      return this.daily.days.reduce((max, d) => Math.max(max, d.booked, d.completed), 0);
    },
    rangeLabel() {
      if (this.fromDate && this.fromDate === this.toDate) return 'on ' + this.formatDay(this.fromDate);
      return 'in this date range';
    },
    pageSummary() {
      return `${this.totalElements} journeys. Filters: ${this.appliedChips.join('; ') || 'none'}`;
    },
    paginationLabel() {
      if (!this.totalElements) return '';
      const from = (this.page - 1) * this.size + 1;
      const to = Math.min(this.page * this.size, this.totalElements);
      const totalPages = Math.max(1, Math.ceil(this.totalElements / this.size));
      const range = from === to ? `${from}` : `${from}–${to}`;
      return `Showing ${range} of ${formatNumber(this.totalElements)} · Page ${this.page} of ${totalPages}`;
    }
  },
  methods: {
    formatNumber,
    prettyStatus,
    prettyJourneyStatus,
    journeyStatusTitle,
    isBooked,
    prettyBookingOutcome,
    bookingOutcomeClass,
    stopReasonClass,
    stopSideLabel,
    prettyStep,
    prettyUserType,
    prettyEntrySurface,
    sourceHint,
    formatSource(row) {
      return formatReferrer(row);
    },
    colClass(key) {
      return this.filteredColumns[key] ? 'trk-col-filtered' : '';
    },
    colTitle(key) {
      return this.filteredColumns[key] || '';
    },
    shortId(id) {
      if (!id) return '-';
      return id.length > 14 ? id.slice(0, 12) + '…' : id;
    },
    journeyLink(sessionId) {
      return {
        path: '/tracking/sessions/' + sessionId,
        query: journeyQuery(this.fromDate, this.toDate, this.filters, { from: 'sessions' })
      };
    },
    openJourney(sessionId) {
      this.$router.push(this.journeyLink(sessionId));
    },
    statusClass(status) {
      if (status === 'ABANDONED') return 'bad';
      if (status === 'IDLE') return 'warn';
      if (status === 'COMPLETED') return 'ok';
      return 'info';
    },
    formatWhen(v) {
      return formatUkDateTime(v);
    },
    formatShort(v) {
      return formatUkDateTime(v, { withSeconds: false });
    },
    formatDay(ymd) {
      if (!ymd) return '-';
      // Noon UTC keeps the calendar day stable whatever the viewer's zone.
      const d = new Date(ymd + 'T12:00:00Z');
      if (Number.isNaN(d.getTime())) return ymd;
      return new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' }).format(d);
    },
    bookingIcon(row) {
      const outcome = bookingOutcome(row);
      if (outcome === 'COMPLETED') return 'fa-check-circle';
      if (outcome === 'BOOKED') return 'fa-calendar-check-o';
      return 'fa-minus-circle';
    },
    barWidth(count) {
      if (!this.dailyMax || !count) return '0%';
      return Math.max(4, Math.round((count / this.dailyMax) * 100)) + '%';
    },
    isSelectedDay(ymd) {
      return !!this.filters.dateBasis && this.fromDate === ymd && this.toDate === ymd;
    },
    /** Reasons only exist for unbooked journeys, so booked / completed views and a reason can't both be on. */
    clearReasonIfBookedView() {
      if (this.filters.booked && this.filters.booked !== 'no') {
        this.filters.stopReason = '';
      }
    },
    clearBookedViewForReason() {
      if (!this.filters.stopReason) return;
      this.filters.dateBasis = '';
      if (this.filters.booked !== 'no') this.filters.booked = '';
    },
    scrollToJourneys() {
      this.$nextTick(() => {
        const el = this.$refs.journeysPanel;
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    onStopReasonChange() {
      this.clearBookedViewForReason();
      this.page = 1;
      this.syncAndLoad();
    },
    onConsultOutcomeChange() {
      this.clearReasonIfBookedView();
      if (this.filters.booked === 'yes') {
        this.filters.dateBasis = 'booked';
      } else if (this.filters.booked === 'completed') {
        this.filters.dateBasis = 'completed';
      } else {
        this.filters.dateBasis = '';
      }
      this.page = 1;
      this.syncAndLoad();
    },
    toggleDateBasis(basis) {
      const activating = this.filters.dateBasis !== basis;
      this.filters.dateBasis = activating ? basis : '';
      if (activating && basis === 'completed') {
        this.filters.booked = 'completed';
      } else if (activating && basis === 'booked') {
        this.filters.booked = 'yes';
      } else if (!activating) {
        if (this.filters.booked === 'completed' || this.filters.booked === 'yes') {
          this.filters.booked = '';
        }
      }
      this.clearReasonIfBookedView();
      this.page = 1;
      this.syncAndLoad();
    },
    showDay(ymd, basis) {
      this.fromDate = ymd;
      this.toDate = ymd;
      this.filters.dateBasis = basis;
      this.filters.booked = basis === 'completed' ? 'completed' : basis === 'booked' ? 'yes' : '';
      this.clearReasonIfBookedView();
      this.page = 1;
      this.syncAndLoad();
    },
    stepName(step) {
      return prettyStep(step);
    },
    toggleStopReason(code) {
      this.filters.stopReason = this.filters.stopReason === code ? '' : code;
      this.clearBookedViewForReason();
      this.page = 1;
      this.syncAndLoad();
      if (this.filters.stopReason) this.scrollToJourneys();
    },
    loadReasons(params) {
      const requestId = ++this.reasonsRequestId;
      this.reasonsLoading = true;
      this.$onboardingTrackingService.getStopReasons(params, { silent: true }).then((res) => {
        if (requestId !== this.reasonsRequestId) return;
        this.reasons = Object.assign(emptyReasons(), (res && res.data) || {});
      }).catch(() => {}).finally(() => {
        if (requestId === this.reasonsRequestId) this.reasonsLoading = false;
      });
    },
    refresh() {
      this.dailyKey = null;
      this.loadSessions();
    },
    loadDaily() {
      // Only the date range and non-booking filters change these counts, not paging.
      const params = this.dailyParams;
      const key = JSON.stringify(params);
      if (key === this.dailyKey) return;
      this.dailyKey = key;
      this.loadReasons(params);
      const requestId = ++this.dailyRequestId;
      this.dailyLoading = true;
      this.$onboardingTrackingService.getDailyBookings(params, { silent: true }).then((res) => {
        if (requestId !== this.dailyRequestId) return;
        this.daily = Object.assign(emptyDaily(), (res && res.data) || {});
      }).catch(() => {
        if (requestId === this.dailyRequestId) this.dailyKey = null;
      }).finally(() => {
        if (requestId === this.dailyRequestId) this.dailyLoading = false;
      });
    },
    onPageChange(page) {
      this.page = page;
      this.syncAndLoad();
    },
    syncAndLoad() {
      const query = journeyQuery(this.fromDate, this.toDate, this.filters);
      this.$router.replace({ query }).catch(() => {});
      this.loadSessions();
    },
    clearFilters() {
      this.filters = emptyFilters();
      this.page = 1;
      this.syncAndLoad();
    },
    callNote(row) {
      const draft = this.callNotes[row.sessionId];
      return draft !== undefined ? draft : (row.callNote || '');
    },
    setCallNote(row, value) {
      this.$set(this.callNotes, row.sessionId, value);
      this.$delete(this.callErrors, row.sessionId);
    },
    isCallNoteDirty(row) {
      const draft = this.callNotes[row.sessionId];
      return draft !== undefined && draft.trim() !== (row.callNote || '').trim();
    },
    isCallSaving(row) {
      return !!this.callSaving[row.sessionId];
    },
    /** Saves the called tick together with the current note (typed or already saved). */
    saveCall(row, called) {
      const id = row.sessionId;
      this.$set(this.callSaving, id, true);
      this.$delete(this.callErrors, id);
      this.$onboardingTrackingService.updateSessionCall(id, { called: !!called, note: this.callNote(row) })
        .then((res) => {
          const updated = (res && res.data) || {};
          row.called = !!updated.called;
          row.callNote = updated.callNote || null;
          row.callUpdatedBy = updated.callUpdatedBy || null;
          row.callUpdatedAt = updated.callUpdatedAt || null;
          this.$delete(this.callNotes, id);
        })
        .catch(() => {
          this.$set(this.callErrors, id, 'Could not save — try again');
        })
        .finally(() => {
          this.$delete(this.callSaving, id);
        });
    },
    downloadJourneys() {
      this.$onboardingTrackingService.exportSessions(this.chatFilters);
    },
    downloadEverything() {
      this.exporting = true;
      this.$onboardingTrackingService
        .exportSessionsBundle(this.chatFilters)
        .catch(() => {})
        .then(() => {
          this.exporting = false;
        });
    },
    loadSessions() {
      this.loadDaily();
      this.loading = true;
      this.$onboardingTrackingService.getSessions({
        ...rangeParams(this.fromDate, this.toDate),
        page: this.page - 1,
        size: this.size,
        sort: 'startedAt,desc',
        status: this.filters.status || undefined,
        step: this.filters.step || undefined,
        q: this.filters.q || undefined,
        firstName: this.filters.firstName || undefined,
        lastName: this.filters.lastName || undefined,
        email: this.filters.email || undefined,
        phone: this.filters.phone || undefined,
        deviceType: this.filters.deviceType || undefined,
        source: this.filters.source || undefined,
        booked: this.filters.booked || undefined,
        dateBasis: this.filters.dateBasis || undefined,
        stopReason: this.filters.stopReason || undefined,
        entrySurface: this.filters.entrySurface || undefined,
        callStatus: this.filters.callStatus || undefined
      }).then((res) => {
        const pageData = (res && res.data) || {};
        this.callNotes = {};
        this.callErrors = {};
        this.sessions = pageData.content || [];
        this.totalElements = pageData.totalElements || 0;
      }).finally(() => { this.loading = false; });
    }
  }
};
</script>

<style scoped>
.trk-filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem 0.65rem;
}
.trk-filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  align-self: start;
}
.trk-filter-field > span {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}
.trk-filter-field-wide {
  grid-column: 1 / -1;
}
.trk-filter-field .trk-toolbar-search {
  flex: none;
  min-width: 0;
}
.trk-booked-ink { color: #0f5d6d; }
.trk-completed-ink { color: #176b3a; }
.trk-outcome-tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.9rem;
}
@media (max-width: 576px) {
  .trk-outcome-tiles { grid-template-columns: 1fr; }
}
.trk-outcome-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  padding: 0.8rem 1rem;
  text-align: left;
  background: #fff;
  border: 1px solid var(--trk-line);
  border-left-width: 4px;
  border-radius: 6px;
  cursor: pointer;
}
.trk-outcome-booked { border-left-color: #0f6a7c; }
.trk-outcome-completed { border-left-color: #1f8a5b; }
.trk-outcome-booked.active { background: #e7f4f7; border-color: #0f6a7c; }
.trk-outcome-completed.active { background: #e7f7ee; border-color: #1f8a5b; }
.trk-outcome-label {
  font-size: 0.84rem;
  font-weight: 700;
}
.trk-outcome-booked .trk-outcome-label { color: #0f5d6d; }
.trk-outcome-completed .trk-outcome-label { color: #176b3a; }
.trk-outcome-value {
  font-size: 1.6rem;
  font-weight: 800;
  line-height: 1.1;
  color: var(--trk-ink);
}
.trk-outcome-help {
  font-size: 0.78rem;
  color: var(--trk-muted);
}
.trk-daily-scroll {
  max-height: 280px;
  overflow-y: auto;
}
.trk-daily-table th,
.trk-daily-table td {
  padding: 0.35rem 0.6rem;
  vertical-align: middle;
}
.trk-daily-table th:first-child,
.trk-daily-table td:first-child {
  width: 120px;
  white-space: nowrap;
}
.trk-daily-selected td {
  background: #f3f8f5;
  font-weight: 700;
}
.trk-daily-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0;
  background: none;
  border: 0;
  text-align: left;
  cursor: pointer;
}
.trk-daily-count:disabled {
  cursor: default;
  opacity: 0.55;
}
.trk-daily-count:not(:disabled):hover .trk-daily-num {
  text-decoration: underline;
}
.trk-daily-bar {
  display: block;
  height: 10px;
  max-width: 70%;
  border-radius: 3px;
}
.trk-daily-bar-booked { background: #0f6a7c; }
.trk-daily-bar-completed { background: #1f8a5b; }
.trk-daily-num {
  font-weight: 700;
  min-width: 2ch;
}
.trk-booking-when {
  margin-top: 0.2rem;
  font-size: 0.74rem;
  color: #64748b;
  white-space: nowrap;
}
.trk-booking-when span {
  font-weight: 700;
}
.trk-reason-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.trk-reason-row {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(80px, 1fr) 90px;
  gap: 0.25rem 0.75rem;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0.65rem;
  text-align: left;
  background: #fff;
  border: 1px solid var(--trk-line);
  border-radius: 6px;
  cursor: pointer;
}
.trk-reason-row:hover { border-color: #9fbfb0; }
.trk-reason-row.active { background: #f3f8f5; border-color: #1f8a5b; }
@media (max-width: 768px) {
  .trk-reason-row { grid-template-columns: 1fr 70px; }
  .trk-reason-bar { display: none; }
}
.trk-reason-main { min-width: 0; }
.trk-reason-label { font-weight: 700; }
.trk-reason-steps {
  margin-left: 0.4rem;
  font-size: 0.78rem;
  color: #64748b;
}
.trk-reason-bar {
  height: 10px;
  border-radius: 3px;
  background: #eef1f0;
  overflow: hidden;
}
.trk-reason-bar > span { display: block; height: 100%; }
.trk-reason-fill-bad { background: #c23b3b; }
.trk-reason-fill-warn { background: #c47b16; }
.trk-reason-fill-neutral { background: #8a9a92; }
.trk-reason-count {
  text-align: right;
  font-weight: 800;
}
.trk-reason-count small {
  font-weight: 600;
  color: #64748b;
}
.trk-reason-details {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 0.9rem;
  font-size: 0.76rem;
  color: #475569;
}
.trk-reason-details span {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.trk-reason-details em { color: #64748b; }
.trk-stop-reason { margin-top: 0.3rem; }
.trk-stop-detail {
  display: block;
  max-width: 260px;
  margin-top: 0.15rem;
  font-size: 0.72rem;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.trk-call-cell {
  min-width: 220px;
}
.trk-call-done {
  background: #f0f8f3;
}
.trk-call-check {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0 0 0.3rem;
  font-weight: 700;
  font-size: 0.8rem;
  white-space: nowrap;
  cursor: pointer;
}
.trk-call-done .trk-call-check { color: #176b3a; }
.trk-call-note {
  font-size: 0.78rem;
  resize: vertical;
}
.trk-call-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.3rem;
}
.trk-call-error {
  font-size: 0.74rem;
  color: #c23b3b;
}
.trk-call-meta {
  margin-top: 0.25rem;
  font-size: 0.72rem;
  color: #64748b;
  white-space: nowrap;
}
.trk-export-help {
  font-size: 0.85rem;
  color: #64748b;
}
.trk-export-help summary {
  cursor: pointer;
  color: #0f766e;
  font-weight: 600;
}
.trk-export-help p {
  margin: 0.35rem 0 0;
}
</style>
